/* eslint-disable max-lines */
/**
 * Design Lab Init Machine Services
 * All async operations (GraphQL, file downloads, etc.)
 */

import { fromPromise } from "xstate";
import {
  GetDesignProjectDocument,
  type GetDesignProjectQueryVariables
} from "graphql-api/operations";
import { mapMadeDesignProjectToGetDesignProjectResponse } from "@design-lab/entities/composition/lib/design-project";
import type {
  LoadProjectInput,
  CreateProjectInput,
  LoadLatestProjectInput,
  DownloadFilesInput,
  ProjectData
} from "./design-lab-init.types";

export const services = {
  // Load existing project by UUID
  loadProject: fromPromise(async ({ input }: { input: LoadProjectInput }): Promise<ProjectData> => {
    const { uuid, apolloClient, sample } = input;

    if (!apolloClient) {
      throw new Error("Apollo client not initialized");
    }

    const result = await apolloClient.query({
      query: GetDesignProjectDocument,
      variables: {
        uuid,
        sample,
        new: false
      }
    });

    if (!result.data?.madeDesignProject) {
      throw new Error(`Project ${uuid} not found`);
    }

    // Transform the GraphQL response to our internal format
    const mapped = mapMadeDesignProjectToGetDesignProjectResponse(result.data.madeDesignProject);
    if (!mapped) {
      throw new Error(`Failed to map project ${uuid}`);
    }
    return mapped;
  }),

  // Load user's latest project for modal preview
  loadLatestProject: fromPromise(
    async ({ input }: { input: LoadLatestProjectInput }): Promise<ProjectData | null> => {
      const { apolloClient } = input;

      try {
        // Same query as loadProject but without UUID to get latest
        const result = await apolloClient.query({
          query: GetDesignProjectDocument,
          variables: {
            sample: false,
            new: false
            // No uuid = latest project
          },
          fetchPolicy: "network-only" // Always get fresh data
        });

        if (!result.data?.madeDesignProject) {
          return null;
        }

        return (
          mapMadeDesignProjectToGetDesignProjectResponse(result.data.madeDesignProject) || null
        );
      } catch {
        // Don't throw - modal should still show even without latest project
        return null;
      }
    }
  ),

  // Create new project
  createProject: fromPromise(
    async ({ input }: { input: CreateProjectInput }): Promise<ProjectData> => {
      const { apolloClient, composition, sample } = input;

      // Build variables with composition if provided
      const variables: GetDesignProjectQueryVariables = {
        sample,
        new: true, // This creates a new project
        // Add composition parameters if provided
        ...(composition && {
          catalogNumber: composition.catalogNumber,
          activeColor: composition.ac
        })
      };

      // Create a new project with composition
      // Note: pdpSizeData (quantities) should be handled separately after project creation
      // using the existing update mechanisms in the frontend
      const result = await apolloClient.query({
        query: GetDesignProjectDocument,
        variables,
        fetchPolicy: "network-only"
      });

      if (!result.data?.madeDesignProject) {
        throw new Error("Failed to create project");
      }

      const mapped = mapMadeDesignProjectToGetDesignProjectResponse(result.data.madeDesignProject);
      if (!mapped) {
        throw new Error("Failed to map created project");
      }
      return mapped;
    }
  ),

  // Download files from external URLs
  downloadExternalFiles: fromPromise(
    async ({ input }: { input: DownloadFilesInput }): Promise<File[]> => {
      const { urls, fileContentType = "image/png" } = input;

      const downloadPromises = urls.map(async (url, index) => {
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to download file from ${url}`);
        }

        const blob = await response.blob();
        const fileName = `external-upload-${index + 1}.${getFileExtension(fileContentType)}`;

        return new File([blob], fileName, { type: fileContentType });
      });

      return Promise.all(downloadPromises);
    }
  )
};

// Helper function for file extension
function getFileExtension(contentType: string): string {
  const typeMap: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/gif": "gif",
    "image/svg+xml": "svg",
    "application/pdf": "pdf"
  };

  return typeMap[contentType] || "png";
}
